/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/core';
import ImageDefault from "../../assets/image/default.png";
import OptionWrapper from "../others/option-wrapper";
import PropTypes from 'prop-types';
import { forwardRef, Fragment, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { animation, borderRadius, displayInlineFlex, itemsCenter, objectCover, overflowHidden, parseWidthHeight, positionRelative } from "../../styles/general";
import { classNames, useIntersection } from "../../utils";
const blurKeyframe = keyframes`
    0% { -webkit-filter: blur(4px);}
    25% { -webkit-filter: blur(3px);}
    50% { -webkit-filter: blur(2px);}
    75% { -webkit-filter: blur(1px);}
    100% { -webkit-filter: blur(0px);}
`;
const Image = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  alt,
  circular,
  className,
  defaultSrc,
  height,
  id,
  lazyLoading,
  onError,
  src,
  style,
  width
}, reference) => {
  if (!reference) reference = useRef(null);
  if (!defaultSrc) defaultSrc = ImageDefault;
  const ref = useRef(null);
  const [srcState, setSrcState] = useState(src);
  const [isInView, setIsInView] = useState(false);
  const [onLoaded, setOnLoaded] = useState(false);
  useIntersection(ref, () => {
    setIsInView(true);
  });
  const _ImageCSS = ImageCSS(width, height, circular);
  useEffect(() => {
    if (src) {
      if (isInView || !lazyLoading) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.onerror = () => {
          setSrcState(defaultSrc);
          onError === null || onError === void 0 ? void 0 : onError();
        };
        img.onload = () => {
          setSrcState(src);
        };
      }
    }
    return () => {
      setSrcState(defaultSrc);
    };
  }, [src]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const renderImage = () => {
    const defaultImage = jsx("img", {
      css: objectCover,
      src: defaultSrc,
      alt: '',
      width: width,
      height: height
    });
    const image = jsx(Fragment, null, !onLoaded && defaultImage, jsx("img", {
      css: [blurAnimationCSS, objectCover],
      src: srcState,
      alt: alt || srcState,
      onLoad: () => setOnLoaded(true),
      width: width,
      height: height
    }));
    if (lazyLoading) {
      return isInView ? image : defaultImage;
    } else return image;
  };
  return useMemo(() => {
    return jsx("div", {
      ref: ref,
      css: _ImageCSS,
      id: id,
      className: classNames('DGN-UI-Image', className),
      style: style
    }, renderImage());
  }, [alt, circular, className, defaultSrc, height, id, lazyLoading, src, style, width, isInView, onLoaded, srcState]);
}));
const ImageCSS = (width, height, circular) => css`
    ${displayInlineFlex};
    ${positionRelative};
    ${itemsCenter};
    ${overflowHidden};
    ${parseWidthHeight(width, height)};
    ${circular && borderRadius('50%')};
`;
const blurAnimationCSS = css`
    ${animation({
  keyframes: blurKeyframe,
  duration: '1s'
})}
`;
Image.defaultProps = {
  circular: false,
  className: '',
  height: 200,
  lazyLoading: false,
  style: {},
  width: 200
};
Image.propTypes = {
  /** Image description. */
  alt: PropTypes.string,
  /** If `true`, image is circular.*/
  circular: PropTypes.bool,
  /** Class for component. */
  className: PropTypes.string,
  /** Default img when src error. */
  defaultSrc: PropTypes.string,
  /** Height of component. */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** If `true`, lazy loading image.*/
  lazyLoading: PropTypes.bool,
  /** Callback fired when the src is error. */
  onError: PropTypes.func,
  /** Image path. */
  src: PropTypes.string,
  /** Style inline of component. */
  style: PropTypes.object,
  /** Width of component. */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
export { Image };
export default OptionWrapper(Image);